import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Tag, RefreshCw, AlertTriangle, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CATEGORY_OPTIONS = [
  { value: 'acougue', label: 'Açougue' },
  { value: 'mercearia', label: 'Mercearia' },
  { value: 'flv', label: 'FLV (Frutas, Legumes e Verduras)' },
  { value: 'padaria', label: 'Padaria' },
  { value: 'bebidas', label: 'Bebidas' },
  { value: 'frios_laticinios', label: 'Frios e Laticínios' },
  { value: 'limpeza', label: 'Limpeza' },
  { value: 'higiene', label: 'Higiene e Perfumaria' },
  { value: 'outros', label: 'Outros' },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

export const UncategorizedReport = ({ onChanged }) => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [fixed, setFixed] = useState({});
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/products/uncategorized-report?days=${days}`, { withCredentials: true });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const fixCategory = async (product, category) => {
    if (!category || category === 'outros') return;
    setSaving((s) => ({ ...s, [product.id]: true }));
    try {
      await axios.put(`${API}/products/${product.id}`, { category }, { withCredentials: true });
      setFixed((f) => ({ ...f, [product.id]: CATEGORY_OPTIONS.find(c => c.value === category)?.label }));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar categoria');
    } finally {
      setSaving((s) => ({ ...s, [product.id]: false }));
    }
  };

  const rows = (data?.products || []).filter(p => !fixed[p.id]);
  const fixedRows = (data?.products || []).filter(p => fixed[p.id]);

  return (
    <div className="bg-white border border-amber-300 rounded-lg p-5 mb-6" data-testid="uncategorized-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Tag size={18} className="text-amber-600" /> Produtos em "Outros" ou sem categoria
          </h2>
          <p className="text-xs text-slate-500">Corrija a categoria direto na lista — o Resumo de Vendas por categoria passa a refletir na hora.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">Vendas dos últimos</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="uncat-days">
            {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>{d} dias</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="uncat-refresh">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3" data-testid="uncat-error">{error}</p>}

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Produtos</p>
            <p className="text-2xl font-black text-amber-800" data-testid="uncat-total-products">{data.total_products}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Qtd vendida</p>
            <p className="text-2xl font-black text-amber-800">{data.total_sold_qty}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Faturado em "Outros"</p>
            <p className="text-2xl font-black text-amber-800">R$ {Number(data.total_revenue).toFixed(2)}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-red-700">Itens sem cadastro</p>
            <p className="text-2xl font-black text-red-800" data-testid="uncat-orphans">{data.orphan_items?.length || 0}</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="uncat-table">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Produto</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Cód. barras / Código</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Categoria atual</th>
              <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-widest text-slate-500">Qtd vendida</th>
              <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-widest text-slate-500">Faturado</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Última venda</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Corrigir para</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-amber-50/40" data-testid={`uncat-row-${p.id}`}>
                <td className="px-3 py-2 text-sm font-medium text-slate-900">{p.name}</td>
                <td className="px-3 py-2 text-xs font-mono text-slate-600">{p.barcode || '—'}{p.code ? <span className="ml-1 text-slate-400">/ {p.code}</span> : null}</td>
                <td className="px-3 py-2 text-xs">
                  {p.category
                    ? <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Outros</span>
                    : <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold">sem categoria</span>}
                </td>
                <td className="px-3 py-2 text-sm text-right font-bold">{p.qty_sold} <span className="text-[10px] text-slate-400">{p.unit}</span></td>
                <td className="px-3 py-2 text-sm text-right">R$ {Number(p.revenue).toFixed(2)}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(p.last_sold)}</td>
                <td className="px-3 py-2">
                  <select
                    defaultValue=""
                    disabled={!!saving[p.id]}
                    onChange={(e) => fixCategory(p, e.target.value)}
                    className="h-8 border border-blue-300 rounded px-2 text-xs bg-white text-blue-800 font-medium"
                    data-testid={`uncat-fix-${p.id}`}
                  >
                    <option value="">Selecionar categoria…</option>
                    {CATEGORY_OPTIONS.filter(c => c.value !== 'outros').map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {fixedRows.map((p) => (
              <tr key={p.id} className="bg-emerald-50" data-testid={`uncat-fixed-${p.id}`}>
                <td className="px-3 py-2 text-sm text-slate-700 line-through">{p.name}</td>
                <td className="px-3 py-2 text-xs font-mono text-slate-500">{p.barcode || '—'}</td>
                <td className="px-3 py-2 text-xs" colSpan={5}>
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><Check size={12} /> Corrigido para {fixed[p.id]}</span>
                </td>
              </tr>
            ))}
            {data && rows.length === 0 && fixedRows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-emerald-700 font-medium" data-testid="uncat-empty">Nenhum produto em "Outros" ou sem categoria. Cadastro em dia!</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.orphan_items?.length > 0 && (
        <div className="mt-5 border border-red-200 rounded-md p-3 bg-red-50/40" data-testid="uncat-orphan-box">
          <p className="text-sm font-bold text-red-800 flex items-center gap-1 mb-2"><AlertTriangle size={14} /> Itens vendidos sem produto cadastrado (caem em "Outros" no resumo) · R$ {Number(data.orphan_revenue).toFixed(2)}</p>
          <p className="text-xs text-slate-600 mb-2">Produto excluído ou venda de item avulso. Cadastre o produto (com o código de barras) para as próximas vendas serem classificadas.</p>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="px-2 py-1">Nome do item</th><th className="px-2 py-1">Código</th><th className="px-2 py-1 text-right">Qtd</th><th className="px-2 py-1 text-right">Faturado</th><th className="px-2 py-1">Última venda</th></tr></thead>
            <tbody className="divide-y divide-red-100">
              {data.orphan_items.map((o, i) => (
                <tr key={i}>
                  <td className="px-2 py-1 font-medium">{o.name}</td>
                  <td className="px-2 py-1 font-mono">{o.barcode || '—'}</td>
                  <td className="px-2 py-1 text-right font-bold">{o.qty_sold}</td>
                  <td className="px-2 py-1 text-right">R$ {Number(o.revenue).toFixed(2)}</td>
                  <td className="px-2 py-1 text-slate-500">{fmtDate(o.last_sold)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
